<template>
    <div class="card">
        <div class="card-header header-elements-inline bg-white">
            <h6 class="card-title"></h6>
            <div class="header-elements">
                <button v-if="upload && !show_upload_form" type="button" class="btn btn-primary ml-auto" @click="showUploadForm">
                    <i class="icon-file-upload mr-2"></i>Ladda upp bild
                </button>
                <button v-if="upload && show_upload_form" type="button" class="btn btn-primary ml-auto" @click="hideUploadForm">
                    <i class="icon-folder-search mr-2"></i>Bläddra
                </button>
            </div>
        </div>
        <div v-if="show_upload_form">
            <div class="card-body">
                <div class="row">
                    <div class="col-6">
                        <select-with-validation
                            vid="category_id"
                            rules="required"
                            component="mediabank-categories-filter"
                            v-model="selected_category_id"
                            @input="formSubmit"
                            label="Kategori"
                            :reduce="(opt) => opt.id"
                            placeholder="Välj kategori..."
                            :disabled="categoryLocked"
                            hide-label
                        ></select-with-validation>
                    </div>
                </div>
            </div>
            <div class="card-body">
                <multiple-file-uploader
                    class="mb-0"
                    url="/vue/mediabank/upload"
                    :meta="{ category_id: selected_category_id }"
                    :disabled="!(selected_category_id > 0)"
                    :message="(!(selected_category_id > 0) ? 'Välj kategori innan du startar uppladdningen!' : '')"
                    multiple
                ></multiple-file-uploader>
            </div>
        </div>
        <div v-else>
            <div class="card-body">
                <div class="row">
                    <div class="col-6">
                        <select-with-validation
                            vid="category_id"
                            component="mediabank-categories-filter"
                            v-model="selected_category_id"
                            @input="formSubmit"
                            label="Kategori"
                            :reduce="(opt) => opt.id"
                            placeholder="--- Alla kategorier ---"
                            :disabled="categoryLocked"
                            hide-label
                        ></select-with-validation>
                    </div>
                    <div class="col-6">
                        <input class="form-control" v-model="search" placeholder="Sök..." @input="formSubmit">
                    </div>
                </div>
            </div>
            <div class="card-body">
                <div v-if="busy_loading" class="position-absolute top-0 left-0 right-0 bottom-0 bg-white opacity-75">
                    <div class="py-5 text-center">
                        <i class="icon-spinner2 spinner"></i>
                    </div>
                </div>
                <div v-if="search_result.meta && search_result.meta.total>0" class="-mb-4">
                    <div class="row">
                        <div v-for="(image, i_idx) in search_result.data" :class="imageClasses" :key="'img-' + i_idx">
                            <a href="#" class="card card-body p-2" @click.prevent="selectImage(image, $event)" :class="{ 'image-selected': selected.includes(image.image_id) }">
                                <div class="image-wrapper mb-2" v-bind:style="{'backgroundImage':'url(' + image.internal_urls.small + ')'}">
                                    <i :id="'imgpop-' + i_idx" class="icon-info22" style="position: absolute; top: 3px; right: 3px; color: #333;"></i>
                                    <b-popover :key="'imgpop-' + i_idx" :target="'imgpop-' + i_idx" triggers="hover focus" placement="bottom">
                                        <div>Originalnamn: {{ image.filename }}</div>
                                        <div v-if="image.width && image.height">Bildupplösning: {{ image.width }}x{{ image.height }} px</div>
                                        <div v-if="image.photographer">Fotograf: {{ image.photographer }}</div>
                                    </b-popover>
                                </div>
                                <div class="text-center" style="min-height: 34px; font-size: 11px; word-wrap: break-word;">{{ image.image_id }}<br>{{ image.name }}</div>
                            </a>
                        </div>
                    </div>
                </div>
                <div v-else>
                    <i>Inga resultat</i>
                </div>
            </div>
            <div v-if="search_result.meta" class="card-footer bg-white">
                <div class="d-flex flex-column flex-md-row align-items-center">
                    <div class="text-center text-md-left">
                        <p class="mb-0">Visar {{ search_result.meta.from }} - {{ search_result.meta.to }} av {{ search_result.meta.total }} bilder</p>
                    </div>
                    <nav class="text-md-right ml-md-auto" aria-label="pagination">
                        <ul class="pagination align-self-center">
                            <li class="page-item" v-bind:class="{ disabled: search_page <= 1 }">
                                <a class="page-link" @click.prevent="btnPreviousPage" href="#">&larr; &nbsp; Föregående</a>
                            </li>
                            <li class="page-item info active">
                                <span class="page-link">Sida {{ search_page }} av {{ search_result.meta.last_page }}</span>
                            </li>
                            <li class="page-item" v-bind:class="{ disabled: search_page >= search_result.meta.last_page }">
                                <a class="page-link" @click.prevent="btnNextPage" href="#">Nästa &nbsp; &rarr;</a>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import { debounce } from 'lodash';
import Vue from 'vue';
import MultipleFileUploader from '@components/MultipleFileUploader';
import SelectWithValidation from '@components/Form/SelectWithValidation.vue';

export default {
    name: 'MediabankBrowser',
    components: {
        SelectWithValidation,
        MultipleFileUploader,
    },
    props: {
        modal: {
            type: Boolean,
            default: false,
        },
        upload: {
            type: Boolean,
            default: false,
        },
        categoryId: {
            type: Number,
            default: 0,
        },
        categoryLocked: {
            type: Boolean,
            default: false,
        },
        selected: {
            type: Array,
            default: () => [],
        },
    },
    data: () => ({
        busy_loading: false,
        selected_category_id: null,
        search: '',
        search_page: 1,
        categories: [],
        search_result: [],
        show_upload_form: false,
        // eslint-disable-next-line func-names
        getImages: debounce(function () {
            const vm = this;
            this.busy_loading = true;
            apiClient.get('/vue/mediabank/images?page=' + this.search_page + '&category_id=' + (this.selected_category_id > 0 ? this.selected_category_id : '') + '&search=' + this.search)
                .then((response) => {
                    Vue.set(vm, 'search_result', response.data);
                }).finally(() => {
                    Vue.set(vm, 'busy_loading', false);
                });
        }, 500),
    }),
    created() {
        if (this.categoryId) {
            this.selected_category_id = this.categoryId;
        }
        this.getImages();
    },
    methods: {
        formSubmit() {
            this.search_page = 1;
            this.getImages();
        },
        selectImage(image, event) {
            if (!this.selected.includes(image.image_id)) {
                this.$emit('select', image, event);
            } else {
                this.$emit('deselect', image, event);
            }
        },
        btnPreviousPage() {
            this.search_page -= 1;
            this.getImages();
        },
        btnNextPage() {
            this.search_page += 1;
            this.getImages();
        },
        showUploadForm() {
            this.show_upload_form = true;
        },
        hideUploadForm() {
            this.getImages();
            this.show_upload_form = false;
        },
    },
    computed: {
        imageClasses() {
            if (this.modal) {
                return ['col-3'];
            }
            return ['col-6', 'col-lg-3', 'col-xl-2'];
        },
    },
};
</script>

<style scoped>
.image-wrapper {
    width: 100%;
    display: block;
    background-color: #ffffff;
    text-align: center;
    position: relative;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
}
.image-wrapper:before {
    content: ' ';
    display: block;
    padding-top: 75%;
}
.image-selected {
    background-color: #fef3ed;
}
</style>
